import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { apiFetch } from '@/lib/http';
import { Loader2, UserCheck, Shield } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/authBootstrap';
import { useToast } from '@/hooks/use-toast';

export default function Onboarding() {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [localUser, setLocalUser] = useState<any>(null);
  const [error, setError] = useState('');
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    async function loadUserData() {
      try {
        // 1. Check Supabase session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          setError('Your session has expired. Please click the invite link again or log in.');
          setLoading(false);
          return;
        }

        setUser(session.user);

        // App identity must come from the authenticated backend.
        const response = await apiFetch('/api/users/me', { credentials: 'omit' });
        if (!response.ok) throw new Error('Unable to verify your account');
        const data = await response.json();
        if (!data?.user?.role) throw new Error('Missing account profile');
        setLocalUser(data.user);

        setLoading(false);
      } catch (err) {
        console.error('[ONBOARDING] Error loading user data:', err);
        setError('Unable to load your account information. Please try again.');
        setLoading(false);
      }
    }

    loadUserData();
  }, []);

  async function handleContinue() {
    setProcessing(true);

    try {
      // 1. Mark onboarding as complete in Supabase user_metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          onboardingCompleted: true,
          onboardingCompletedAt: new Date().toISOString()
        }
      });

      if (metadataError) throw metadataError;

      // Do not announce success or navigate until the DB gate is persisted.
      const response = await apiFetch('/api/auth/complete-onboarding', {
        method: 'POST',
        credentials: 'omit',
      });
      if (!response.ok) throw new Error('Could not save onboarding completion');

      // 3. Determine destination based on role
      const role = localUser.role;

      let destination = '/';
      if (role === 'admin' || role === 'superadmin' || role === 'super_admin') {
        destination = '/campaigns';
      } else {
        destination = '/mobile/cycle-selector';
      }

      console.log('[ONBOARDING] Redirecting to:', destination, 'for role:', role);

      toast({
        title: 'Welcome to ReActor!',
        description: 'Your account is ready to use.',
      });

      // Small delay to show toast
      setTimeout(() => {
        window.location.assign(destination);
      }, 500);

    } catch (err) {
      console.error('[ONBOARDING] Error completing onboarding:', err);
      toast({
        title: 'Error',
        description: 'Could not complete setup. Please try again.',
        variant: 'destructive',
      });
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-gray-600">Loading your account...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Session Expired</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => setLocation('/login')}
              variant="default"
              className="w-full"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Extract user information
  const name = localUser?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';
  const role = localUser.role;
  const email = localUser?.email || user?.email || '';

  // Format role for display
  const roleDisplay = role === 'superadmin' || role === 'super_admin'
    ? 'Super Admin'
    : role.charAt(0).toUpperCase() + role.slice(1);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <UserCheck className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Welcome to ReActor! 👋</CardTitle>
          <CardDescription>
            Your account has been created and is ready to use
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* User Info */}
          <div className="space-y-3">
            {name && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Welcome</p>
                <p className="font-semibold text-gray-900">{name}</p>
              </div>
            )}

            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-semibold text-gray-900">{email}</p>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
              <Shield className="h-4 w-4 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Your Role</p>
                <p className="font-semibold text-gray-900">{roleDisplay}</p>
              </div>
            </div>
          </div>

          {/* Quick Info */}
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              {role === 'operator' && (
                <>You can now view and complete your assigned tasks.</>
              )}
              {(role === 'admin' || role === 'superadmin' || role === 'super_admin') && (
                <>You have full access to manage campaigns, users, and assignments.</>
              )}
            </p>
          </div>

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            disabled={processing}
            className="w-full"
            size="lg"
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Setting up...
              </>
            ) : (
              'Get Started'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

